import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import Line from "../Line";
import { CButton } from "@carrefour/design-system-components-react";
import { extractDateTime, getSafeDate } from "../../utils/dateUtils";
import { isValidUrl } from "../../utils/fetcher";
import { Communication } from "../../types/Communication";
import { ReactComponent as CommIcon } from "../../media/comm-bandeau.svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fr } from "date-fns/locale";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";


const StyledInput = styled.input`
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  box-sizing: border-box;

  &[type="date"]:not(.has-value) {
    color: transparent;
    caret-color: transparent;
    &::-webkit-datetime-edit { color: transparent; }
  }
  &[type="date"].has-value {
    color: inherit;
  }
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;

const Footer = styled.div`
  margin-top: auto;
  text-align: right;
  width: 100%;
`;

const StyledLabel = styled.label`
  display: block;
  font-size: 0.850rem;
  font-weight: 500;
  margin-bottom: 0;
  text-align: left;
  strong {
    font-size: 0.75rem;
  }
`;

const FormContainer = styled.div`
  width: 98%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  flex: 1;
  padding: 4px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledSelect = styled.select`
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  box-sizing: border-box;
  background-color: white;
  max-width: 100%;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  box-sizing: border-box;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
`;

const Banner = styled.div`
  background-color: #E6F1FE;
  padding: 4px 8px;
  border-radius: 4px;
  margin-top: 4px;
  text-align: left;
  font-size: 0.85rem;
`;

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const CharCounter = styled.span`
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 12px;
  color: #131212;
  pointer-events: none;
`;

const DatePickerWrapper = styled.div`
  width: 100%;
  & .react-datepicker-wrapper {
    width: 100%;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1400;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 400px;
  max-width: 90%;
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.5rem;
  gap: 1rem;
  align-items: stretch;

  & > * {
    width: 100%;
  }

  @media (min-width: 769px) {
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;

    & > * {
      width: auto;
    }
  }
`;

const CommunicationEditForm = ({ initialData, onSave, onCancel, onDelete }: { initialData: Communication | null, onSave: (data: Communication) => void, onCancel: () => void, onDelete?: (id: string) => void }) => {
  const [communicationData, setCommunicationData] = useState<Communication>(() => {
    if (initialData) {
      return {
        ...initialData,
        startDate: initialData.startDate ? initialData.startDate.replace('T', ' ').slice(0, 16) : '',
        endDate: initialData.endDate ? initialData.endDate.replace('T', ' ').slice(0, 16) : ''
      };
    }
    return {
      id: '',
      title: '',
      text: '',
      link: '',
      type: 'INFORMATION',
      startDate: '',
      endDate: ''
    };
  });

  const [mode, setMode] = useState(initialData?.link ? "with" : "without");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (initialData && String(initialData.id) !== String(communicationData.id)) {
      setCommunicationData({
        ...initialData,
        startDate: initialData.startDate ? initialData.startDate.replace('T', ' ').slice(0, 16) : '',
        endDate: initialData.endDate ? initialData.endDate.replace('T', ' ').slice(0, 16) : ''
      });
      setMode(initialData.link ? "with" : "without");
    }
  }, [initialData, communicationData.id]);

    const communicationDataRef = useRef(communicationData);

  useEffect(() => {
    communicationDataRef.current = communicationData;
  }, [communicationData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCommunicationData(prev => {
      const newData = { ...prev, [name]: value };
      return newData;
    });
  };

  const handleDateChange = (name: string, value: Date | null) => {
    if (!value || isNaN(value.getTime())) {
      return;
    }
    setCommunicationData(prev => ({ ...prev, [name]: extractDateTime(value) }));
  };

  const askToDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (onDelete && communicationData.id) {
      onDelete(communicationData.id);
    }
    setShowDeleteModal(false);
  };

  const handleModeChange = (value:string) => {
    setMode(value);

    // Optional: clear link when switching to "without"
    if (value === "without") {
      setCommunicationData(prev => ({ ...prev, link: "" }));
    }
  };

  const startDateObj = getSafeDate(communicationData.startDate);
  const endDateObj = getSafeDate(communicationData.endDate);
  const isDateOrderValid = !startDateObj || !endDateObj || startDateObj <= endDateObj;

  const isFormValid =
    communicationData.title &&
    communicationData.text &&
    communicationData.startDate &&
    communicationData.endDate &&
    (mode === "without" || (communicationData.link && isValidUrl(communicationData.link))) &&
    isDateOrderValid;
  
  const {t} = useTranslation();

  return (
    <>
      <Title>{'Modifier une communication'}</Title>
      <FormContainer>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <StyledLabel><strong>Aperçu</strong></StyledLabel>
            <FormGroup>
              <StyledLabel>Type *</StyledLabel>
                <StyledSelect
                  name="type"
                  value={communicationData.type}
                  onChange={handleInputChange}
                >
                  <option value="INFORMATION" >Information</option>
                  <option value="ALERT" >Alerte</option>
                </StyledSelect>
            </FormGroup>
          </div>
          {communicationData.title && (
            <Banner style={{ 
              display: "flex", alignItems: "center", gap: "10px", 
              backgroundColor: communicationData.type === 'ALERT' ? '#faafb7' : '#E6F1FE'
            }}>
              <CommIcon height={40} width={40} />
              <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <span style={{ fontWeight: "bold" }}>{communicationData.title}</span>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <span>{communicationData.text}</span>
                  {communicationData.link && (
                    <Link data-testid="linklocal" 
                          to={`${communicationData.link}`} 
                          style={{color: 'black', fontWeight: 'bold'}}
                          target="_blank"
                          rel="noopener noreferrer"
                    >
                      {t('communication.link')}
                    </Link>
                  )}
                </div>
              </div>
            </Banner> 
          )}
        <StyledLabel><strong> Contenu:</strong></StyledLabel>
        <FormGroup>
          <StyledLabel>{t('communication.title')} *</StyledLabel>
          <StyledInput name="title" value={communicationData.title} onChange={handleInputChange} placeholder={t('communication.title_placeholder')} maxLength={30} />
        </FormGroup>
        <FormGroup>
          <StyledLabel>Texte *</StyledLabel>
          <TextAreaWrapper>
            <StyledTextArea
              name="text"
              value={communicationData.text}
              onChange={handleInputChange}
              placeholder={t('communication.text_placeholder')}
              maxLength={80}
            />
            <CharCounter>
              {communicationData.text.length}/80
            </CharCounter>
          </TextAreaWrapper>
          <FormGroup>
            <div style={{ display: 'flex', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', fontWeight: 500 }}>
                <input 
                  type="radio" 
                  name="linkMode" 
                  value="with" 
                  checked={mode === "with"} 
                  onChange={(e) => handleModeChange(e.target.value)}
                />
                {t('communication.with_link')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', fontWeight: 500 }}>
                <input 
                  type="radio" 
                  name="linkMode" 
                  value="without" 
                  checked={mode === "without"} 
                  onChange={(e) => handleModeChange(e.target.value)}
                />
                {t('communication.without_link')}
              </label>
            </div>
          </FormGroup>

          {mode === "with" && (
          <FormGroup>
            <StyledLabel>{t('communication.link')}</StyledLabel>
            <StyledInput name="link" value={communicationData.link} onChange={handleInputChange} placeholder="Lien"  />
            {communicationData.link && !isValidUrl(communicationData.link) && (
              <StyledLabel style={{ color: 'red' }}>{t('communication.link_nonvalid')} (ex: https://www.example.com)</StyledLabel>
            )}
          </FormGroup>
          )}
        </FormGroup>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <StyledLabel><strong> Durée d'affichage:</strong></StyledLabel>
          <div style={{ display: 'flex', gap: '16px' }}>
            <FormGroup style={{ flex: 1 }}>
              <StyledLabel>{t('communication.start_date')} *</StyledLabel>
              <DatePickerWrapper>
                <DatePicker
                  selected={getSafeDate(communicationData.startDate)}
                  onChange={(date: Date | null) => handleDateChange('startDate', date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="JJ/MM/AAAA"
                  customInput={<StyledInput />}
                  locale={fr}
                />
              </DatePickerWrapper>
            </FormGroup>
            <FormGroup style={{ flex: 1 }}>
              <StyledLabel>{t('communication.end_date')} *</StyledLabel>
              <DatePickerWrapper>
                <DatePicker
                  selected={getSafeDate(communicationData.endDate)}
                  onChange={(date: Date | null) => handleDateChange('endDate', date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="JJ/MM/AAAA"
                  customInput={<StyledInput />}
                  locale={fr}
                />
              </DatePickerWrapper>
            </FormGroup>
          </div>
        </div>
        {!isDateOrderValid && (
          <StyledLabel style={{ color: 'red' }}>{t('communication.err_date')}</StyledLabel>
        )}
        {initialData && onDelete && (
          <CButton
            key="delete"
            onClick={askToDelete}
            size="s"
            variation="secondary"
          >
            {t('communication.delete')}
          </CButton> )}
      </FormContainer>
      <Footer>
        <Line />
        <ButtonGroup>
          <CButton onClick={onCancel} type="button" variation="secondary" size="m">
            {t('communication.cancel')}
          </CButton>
          <CButton 
            key="save"
            onClick={() => {
              onSave(communicationDataRef.current);
            }} 
            size="m" 
            variation="primary" 
            disabled={!isFormValid}
          >
            {t('communication.save')}
          </CButton> 
        </ButtonGroup>
      </Footer>
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <Title>{t('communication.popup_title')}</Title>
            <p>{t('communication.popup_text')} ?</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
              <CButton onClick={() => setShowDeleteModal(false)} size="s" variation="secondary">
                {t('communication.cancel')}
              </CButton>
              <CButton onClick={confirmDelete} size="s" variation="primary">
                {t('communication.confirm')}
              </CButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default CommunicationEditForm;